import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  FormLabel,
  FormHelperText,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stepper,
  Step,
  StepLabel,
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Skeleton,
  Switch,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Memory as MemoryIcon,
  Add as AddIcon,
  Close as CloseIcon,
  ImageOutlined as ImageIcon,
  RecordVoiceOverOutlined as VoiceIcon,
  VideocamOutlined as VideoIcon,
  SpeedOutlined as SpeedIcon,
  AutoAwesomeOutlined as MultimodalIcon,
  DataArrayOutlined as EmbeddingIcon,
} from "@mui/icons-material";
import DashboardLayout from "../components/DashboardLayout";
import { useAuth } from "../contexts/AuthContext";
import { deploymentApi, credentialsApi, usageApi } from "../services/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

// Removed hardcoded GPU pricing data since we'll get it from the API

const CreateDeployment = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    getValues,
    setError: setFormError,
    clearErrors,
  } = useForm({
    defaultValues: {
      name: "",
      model_source: "hf",
      model_url: "",
      max_replicas: 1,
      is_llm: true,
      gpu_count: 1,
      gpu_type: "", // New field for gpu type
      memory_request: "16Gi",
      memory_limit: "16Gi",
      cpu_request: "1",
      cpu_limit: "2",
      storage: "15Gi", // New storage field
      deeplearning: {
        input_size: "1 3 224 224",
        latency_constraint: 100,
        throughput_constraint: 60,
        gpu_used_memory: 4000,
        max_batch_size: 8,
      },
      llm: {
        is_embedding: false,
        is_multimodal: false,
        is_image: false,
        is_voice: false,
        is_video: false,
        speculative_model: false,
        is_matryoshka: false,
        matryoshka_dimensions: [],
      },
      credential_id: "",
      // Keep the old fields for backward compatibility, but they won't be used directly
      aws_access_key_id: "",
      aws_secret_access_key: "",
      aws_region: "us-east-1",
      hf_token: "",
    },
  });

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [openCredentialDialog, setOpenCredentialDialog] = useState(false);
  const [newCredential, setNewCredential] = useState({
    name: "",
    source: "hf",
    data: { token: "" }, // for HF
  });
  const [creatingCredential, setCreatingCredential] = useState(false);
  const [credentialFormErrors, setCredentialFormErrors] = useState({});
  const [isCredentialFormValid, setIsCredentialFormValid] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [matryoshkaDimensionInput, setMatryoshkaDimensionInput] = useState("");

  // New states for GPU rates
  const [gpuRates, setGpuRates] = useState([]);
  const [loadingGpuRates, setLoadingGpuRates] = useState(false);

  const modelSource = watch("model_source");
  const isLLM = watch("is_llm");
  const gpuCount = watch("gpu_count");
  const gpuType = watch("gpu_type");
  const isEmbedding = watch("llm.is_embedding");
  const isMatryoshka = watch("llm.is_matryoshka");
  const matryoshkaDimensions = watch("llm.matryoshka_dimensions") || [];

  const steps = [
    t("createDeployment.modelDetails"),
    t("createDeployment.resourceConfiguration"),
    t("createDeployment.credentials"),
    t("createDeployment.reviewAndDeploy"),
  ];

  // Fetch GPU rates when component mounts
  useEffect(() => {
    const fetchGpuRates = async () => {
      setLoadingGpuRates(true);
      try {
        const response = await usageApi.getGpuRates();
        if (
          response.data &&
          response.data.items &&
          response.data.items.length > 0
        ) {
          setGpuRates(response.data.items);

          // Set the default GPU type to the first one in the list
          setValue("gpu_type", response.data.items[0].gpu_type);
        } else {
          toast.error(t("deployments.noGpuOptions"));
        }
      } catch (error) {
        console.error("Error fetching GPU rates:", error);
        toast.error(t("deployments.failedToFetchGpu"));
      } finally {
        setLoadingGpuRates(false);
      }
    };

    fetchGpuRates();
  }, [setValue]);

  useEffect(() => {
    if (!isEmbedding) {
      setValue("llm.is_matryoshka", false);
    }
  }, [isEmbedding, setValue]);

  useEffect(() => {
    if (!isEmbedding || !isMatryoshka) {
      setValue("llm.matryoshka_dimensions", []);
      setMatryoshkaDimensionInput("");
      clearErrors("llm.matryoshka_dimensions");
    }
  }, [clearErrors, isEmbedding, isMatryoshka, setValue]);

  // Fetch credentials when model source changes
  useEffect(() => {
    const fetchCredentials = async () => {
      if (modelSource && (modelSource === "hf" || modelSource === "s3")) {
        setLoadingCredentials(true);
        try {
          const response = await credentialsApi.getCredentials(modelSource);
          setCredentials(response.data);
          // Clear credential selection when source changes
          setValue("credential_id", "");
        } catch (error) {
          console.error("Error fetching credentials:", error);
          toast.error(t("deployments.failedToFetchCredentials"));
          setCredentials([]);
        } finally {
          setLoadingCredentials(false);
        }
      } else {
        setCredentials([]);
      }
    };

    fetchCredentials();
  }, [modelSource, setValue]);

  const handleNext = () => {
    if (activeStep === 0 && !commitPendingMatryoshkaDimensions()) {
      return;
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleOpenCredentialDialog = () => {
    // Set the source based on current model source
    setNewCredential({
      name: "",
      source: modelSource,
      data:
        modelSource === "hf"
          ? { token: "" }
          : {
              aws_access_key_id: "",
              aws_secret_access_key: "",
              region: "us-east-1",
              url_endpoint: "",
            },
    });
    setCredentialFormErrors({});
    setTouchedFields({});
    setIsCredentialFormValid(false);
    setOpenCredentialDialog(true);
  };

  const handleCloseCredentialDialog = () => {
    setOpenCredentialDialog(false);
  };

  const parseMatryoshkaDimensionTokens = (input) => {
    const tokens = input
      .split(/[\s,]+/)
      .map((token) => token.trim())
      .filter(Boolean);

    if (!tokens.length) {
      return { values: [] };
    }

    const parsedValues = [];

    for (const token of tokens) {
      if (!/^\d+$/.test(token) || Number(token) <= 0) {
        return {
          error: t("createDeployment.matryoshkaDimensionsInvalid", {
            value: token,
          }),
        };
      }

      parsedValues.push(Number(token));
    }

    return { values: parsedValues };
  };

  const commitPendingMatryoshkaDimensions = (
    rawInput = matryoshkaDimensionInput,
  ) => {
    if (!isEmbedding || !isMatryoshka) {
      clearErrors("llm.matryoshka_dimensions");
      return true;
    }

    const trimmedInput = rawInput.trim();

    if (!trimmedInput) {
      clearErrors("llm.matryoshka_dimensions");
      return true;
    }

    const parsedResult = parseMatryoshkaDimensionTokens(trimmedInput);

    if (parsedResult.error) {
      setFormError("llm.matryoshka_dimensions", {
        type: "manual",
        message: parsedResult.error,
      });
      return false;
    }

    const nextDimensions = Array.from(
      new Set([...(matryoshkaDimensions || []), ...parsedResult.values]),
    ).sort((left, right) => left - right);

    setValue("llm.matryoshka_dimensions", nextDimensions, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setMatryoshkaDimensionInput("");
    clearErrors("llm.matryoshka_dimensions");

    return true;
  };

  const removeMatryoshkaDimension = (dimensionToRemove) => {
    const nextDimensions = matryoshkaDimensions.filter(
      (dimension) => dimension !== dimensionToRemove,
    );

    setValue("llm.matryoshka_dimensions", nextDimensions, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (!nextDimensions.length) {
      clearErrors("llm.matryoshka_dimensions");
    }
  };

  const buildLlmPayload = (llmConfig) => {
    const llmPayload = {
      is_embedding: Boolean(llmConfig?.is_embedding),
      is_multimodal: Boolean(llmConfig?.is_multimodal),
      is_image: Boolean(llmConfig?.is_image),
      is_voice: Boolean(llmConfig?.is_voice),
      is_video: Boolean(llmConfig?.is_video),
      speculative_model: Boolean(llmConfig?.speculative_model),
    };

    if (!llmPayload.is_embedding) {
      return { llmPayload };
    }

    if (!llmConfig?.is_matryoshka) {
      return { llmPayload };
    }

    const dimensions = Array.isArray(llmConfig?.matryoshka_dimensions)
      ? llmConfig.matryoshka_dimensions
      : [];

    if (dimensions.length) {
      const parsedDimensions = parseMatryoshkaDimensionTokens(
        dimensions.join(","),
      );

      if (parsedDimensions.error || !parsedDimensions.values.length) {
        return {
          error:
            parsedDimensions.error ||
            t("createDeployment.matryoshkaDimensionsRequired"),
        };
      }

      llmPayload.matryoshka_dimensions = parsedDimensions.values;
    }

    if (llmConfig?.is_matryoshka) {
      llmPayload.is_matryoshka = true;
    }

    return { llmPayload };
  };

  const validateCredentialForm = (updatedCredential, fieldName = null) => {
    const errors = { ...credentialFormErrors };

    // If a fieldName is provided, only validate that field
    // Otherwise validate all touched fields
    const fieldsToValidate = fieldName
      ? [fieldName]
      : Object.keys(touchedFields).filter((field) => touchedFields[field]);

    // Don't show errors for fields the user hasn't interacted with yet
    if (fieldsToValidate.includes("name") && !updatedCredential.name.trim()) {
      errors.name = t("createDeployment.credentialNameRequired");
    } else if (fieldsToValidate.includes("name")) {
      delete errors.name;
    }

    // Source-specific validations
    if (updatedCredential.source === "hf") {
      if (
        fieldsToValidate.includes("token") &&
        !updatedCredential.data.token.trim()
      ) {
        errors.token = t("createDeployment.huggingFaceTokenRequired");
      } else if (fieldsToValidate.includes("token")) {
        delete errors.token;
      }
    } else if (updatedCredential.source === "s3") {
      if (
        fieldsToValidate.includes("aws_access_key_id") &&
        !updatedCredential.data.aws_access_key_id.trim()
      ) {
        errors.aws_access_key_id = t("createDeployment.awsAccessKeyRequired");
      } else if (fieldsToValidate.includes("aws_access_key_id")) {
        delete errors.aws_access_key_id;
      }

      if (
        fieldsToValidate.includes("aws_secret_access_key") &&
        !updatedCredential.data.aws_secret_access_key.trim()
      ) {
        errors.aws_secret_access_key = t(
          "createDeployment.awsSecretAccessKeyRequired",
        );
      } else if (fieldsToValidate.includes("aws_secret_access_key")) {
        delete errors.aws_secret_access_key;
      }
      // url_endpoint is optional, no validation needed
    }

    setCredentialFormErrors(errors);

    // Check if all required fields are valid - regardless of whether they've been touched
    const requiredFieldsValid =
      updatedCredential.name.trim() !== "" &&
      (updatedCredential.source === "hf"
        ? updatedCredential.data.token.trim() !== ""
        : updatedCredential.data.aws_access_key_id.trim() !== "" &&
          updatedCredential.data.aws_secret_access_key.trim() !== "");

    setIsCredentialFormValid(requiredFieldsValid);

    return errors;
  };

  const handleCredentialInputChange = (e) => {
    const { name, value } = e.target;

    // Mark field as touched
    const fieldName = name.startsWith("data.") ? name.split(".")[1] : name;
    setTouchedFields((prev) => ({
      ...prev,
      [fieldName]: true,
    }));

    let updatedCredential;
    if (name.startsWith("data.")) {
      const dataField = name.split(".")[1];
      updatedCredential = {
        ...newCredential,
        data: {
          ...newCredential.data,
          [dataField]: value,
        },
      };
    } else {
      updatedCredential = {
        ...newCredential,
        [name]: value,
      };
    }

    setNewCredential(updatedCredential);
    validateCredentialForm(updatedCredential, fieldName);
  };

  const handleCreateCredential = async () => {
    // Mark all fields as touched before submission
    const allFields = ["name"];
    if (modelSource === "hf") {
      allFields.push("token");
    } else if (modelSource === "s3") {
      allFields.push("aws_access_key_id", "aws_secret_access_key");
    }

    const allTouched = allFields.reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {});

    setTouchedFields(allTouched);

    // Validate all fields
    const errors = validateCredentialForm(newCredential);

    // Only proceed if there are no errors
    if (Object.keys(errors).length > 0) {
      return;
    }

    setCreatingCredential(true);
    try {
      // Structure the payload according to the required format
      const credentialPayload = {
        name: newCredential.name,
        source: newCredential.source,
        credentials: newCredential.data, // Rename data to credentials
        is_active: true,
      };

      const response =
        await credentialsApi.createCredentials(credentialPayload);
      toast.success(t("deployments.credentialsCreated"));
      setCredentials((prev) => [...prev, response.data]);
      setValue("credential_id", response.data.id);
      handleCloseCredentialDialog();

      // Refresh the credentials list
      const refreshResponse = await credentialsApi.getCredentials(modelSource);
      setCredentials(refreshResponse.data);
    } catch (error) {
      console.error("Error creating credentials:", error);
      toast.error(
        error.response?.data?.detail ||
          t("deployments.failedToCreateCredentials"),
      );
    } finally {
      setCreatingCredential(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setError(null);

    try {
      if (!commitPendingMatryoshkaDimensions()) {
        setActiveStep(0);
        setLoading(false);
        return;
      }

      // Use a default value of false if GCP_AUTOPILOT is undefined
      const autopilotValue =
        window.env?.GCP_AUTOPILOT !== undefined
          ? typeof window.env.GCP_AUTOPILOT === "string"
            ? window.env.GCP_AUTOPILOT.toLowerCase() === "true"
            : Boolean(window.env.GCP_AUTOPILOT)
          : false; // Default to false if undefined

      // Prepare deployment data
      const deploymentData = {
        name: data.name,
        model_source: data.model_source,
        model_url: data.model_url,
        max_replicas: data.max_replicas,
        args: {
          is_llm: data.is_llm,
        },
        resources: {
          gpu_count: data.gpu_count,
          gpu_type: data.gpu_type,
          memory_request: data.memory_request,
          memory_limit: data.memory_limit,
          cpu_request: data.cpu_request,
          cpu_limit: data.cpu_limit,
          storage: data.storage,
          autopilot: autopilotValue,
        },
      };

      // Only add the relevant model type configuration
      if (data.is_llm) {
        const { llmPayload, error: llmPayloadError } = buildLlmPayload(
          getValues("llm"),
        );

        if (llmPayloadError) {
          setFormError("llm.matryoshka_dimensions", {
            type: "manual",
            message: llmPayloadError,
          });
          setActiveStep(0);
          setLoading(false);
          return;
        }

        deploymentData.args.llm = llmPayload;
      } else {
        deploymentData.args.deeplearning = data.deeplearning;
      }

      // Add credential information to the payload:
      // 1. For Hugging Face: credentials are optional (for public models) and should be OMITTED from payload when not provided
      // 2. For S3: credentials are required
      // 3. For HTTP: no credentials needed
      if (data.credential_id) {
        // Find the credential name based on id
        const selectedCredential = credentials.find(
          (cred) => cred.id === data.credential_id,
        );
        if (selectedCredential && selectedCredential.name) {
          // Only include credentials field in the payload when a credential is selected
          deploymentData.credentials = selectedCredential.name;
        }
      } else if (data.model_source === "s3") {
        // S3 requires credentials, but this case should be caught by form validation
        setError(t("createDeployment.credentialsRequiredForS3"));
        setLoading(false);
        return;
      }

      // Add user info
      deploymentData.user = {
        name: currentUser.displayName || "",
        email: currentUser.email,
        organization: "",
      };

      // Create deployment
      const response = await deploymentApi.createDeployment(deploymentData);

      toast.success(t("deployments.deploymentCreated"));
      navigate(`/deployments/${response.data.model_id}`);
    } catch (error) {
      console.error("Error creating deployment:", error);
      setError(
        error.response?.data?.detail ||
          t("deployments.failedToCreateDeployment"),
      );
      toast.error(t("deployments.failedToCreateDeployment"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitDeployment = () => {
    if (!commitPendingMatryoshkaDimensions()) {
      setActiveStep(0);
      return;
    }

    handleSubmit(onSubmit)();
  };

  // Calculate estimated cost based on selected GPU type and count
  const calculateHourlyCost = () => {
    if (!gpuType || !gpuCount) return 0;

    const selectedGpu = gpuRates.find((rate) => rate.gpu_type === gpuType);
    if (!selectedGpu) return 0;

    return selectedGpu.hourly_rate * gpuCount;
  };

  // Render step content
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("deployments.modelInformation")}
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                rules={{ required: t("createDeployment.modelNameRequired") }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("deployments.modelName")}
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    placeholder={t("deployments.egMyBertModel")}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="model_source"
                control={control}
                render={({ field }) => (
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      {t("deployments.modelSource")}
                    </FormLabel>
                    <RadioGroup row {...field}>
                      <FormControlLabel
                        value="hf"
                        control={<Radio />}
                        label={t("deployments.huggingFace")}
                      />
                      <FormControlLabel
                        value="s3"
                        control={<Radio />}
                        label={t("deployments.s3Bucket")}
                      />
                      {/* SECURITY FIX: Commented out HTTP URL option to prevent SSRF vulnerability */}
                      {/* <FormControlLabel value="http" control={<Radio />} label="HTTP URL" /> */}
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="model_url"
                control={control}
                rules={{
                  required: t("createDeployment.modelUrlRequired"),
                  validate: {
                    validHfRepo: (value) => {
                      if (modelSource === "hf" && !value.includes("/")) {
                        return t("createDeployment.validHuggingFaceRepo");
                      }
                      return true;
                    },
                    validS3: (value) => {
                      if (modelSource === "s3" && !value.startsWith("s3://")) {
                        return t("createDeployment.s3UrlMustStart");
                      }
                      return true;
                    },
                    // SECURITY FIX: Commented out HTTP URL validation to prevent SSRF vulnerability
                    // validHttp: value => {
                    //   if (modelSource === 'http' && !value.startsWith('http')) {
                    //     return 'HTTP URL must start with http:// or https://';
                    //   }
                    //   return true;
                    // }
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className="url-textfield"
                    label={
                      modelSource === "hf"
                        ? t("deployments.huggingFaceRepo")
                        : modelSource === "s3"
                          ? t("deployments.s3Url")
                          : t("deployments.modelUrl")
                    }
                    fullWidth
                    error={!!errors.model_url}
                    helperText={
                      errors.model_url?.message ||
                      (modelSource === "hf"
                        ? "e.g., username/model-name"
                        : modelSource === "s3"
                          ? "e.g., s3://bucket-name/model-path"
                          : t("createDeployment.enterModelUrl"))
                    }
                    placeholder={
                      modelSource === "hf"
                        ? "username/model-name"
                        : modelSource === "s3"
                          ? "s3://bucket-name/model-path"
                          : "Model URL"
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="is_llm"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      {t("deployments.modelType")}
                    </FormLabel>
                    <RadioGroup
                      row
                      {...field}
                      value={value.toString()}
                      onChange={(e) => onChange(e.target.value === "true")}
                    >
                      <FormControlLabel
                        value="false"
                        control={<Radio />}
                        label={t("createDeployment.computerVisionDeepLearning")}
                      />
                      <FormControlLabel
                        value="true"
                        control={<Radio />}
                        label={t("deployments.llm")}
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="max_replicas"
                control={control}
                rules={{
                  required: t("createDeployment.replicasRequired"),
                  min: {
                    value: 1,
                    message: t("createDeployment.minOneReplica"),
                  },
                  max: {
                    value: 10,
                    message: t("createDeployment.maxTenReplicas"),
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("deployments.maximumReplicas")}
                    type="number"
                    fullWidth
                    InputProps={{ inputProps: { min: 1, max: 10 } }}
                    error={!!errors.max_replicas}
                    helperText={
                      errors.max_replicas?.message ||
                      t("deployments.maximumReplicasHelper")
                    }
                  />
                )}
              />
            </Grid>

            {isLLM && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  {t("deployments.llmCapabilities")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t("deployments.llmCapabilitiesHelper")}
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} sm={4} md={2}>
                    <Controller
                      name="llm.is_embedding"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Card
                          sx={{
                            cursor: "pointer",
                            border: value ? "2px solid" : "1px solid",
                            borderColor: value ? "primary.main" : "divider",
                            height: "100%",
                            transition: "all 0.2s",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            },
                          }}
                          onClick={() => onChange(!value)}
                        >
                          <CardContent
                            sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <EmbeddingIcon
                                sx={{
                                  fontSize: 32,
                                  mb: 1,
                                  color: value
                                    ? "primary.main"
                                    : "text.secondary",
                                }}
                              />
                              <Typography
                                variant="body2"
                                align="center"
                                fontWeight={500}
                              >
                                {t("deployments.embedding")}
                              </Typography>
                              {value && (
                                <Chip
                                  label={t("createDeployment.selected")}
                                  color="primary"
                                  size="small"
                                  sx={{ mt: 1, height: 20, fontSize: "0.7rem" }}
                                />
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Controller
                      name="llm.is_multimodal"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Card
                          sx={{
                            cursor: "pointer",
                            border: value ? "2px solid" : "1px solid",
                            borderColor: value ? "primary.main" : "divider",
                            height: "100%",
                            transition: "all 0.2s",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            },
                          }}
                          onClick={() => onChange(!value)}
                        >
                          <CardContent
                            sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <MultimodalIcon
                                sx={{
                                  fontSize: 32,
                                  mb: 1,
                                  color: value
                                    ? "primary.main"
                                    : "text.secondary",
                                }}
                              />
                              <Typography
                                variant="body2"
                                align="center"
                                fontWeight={500}
                              >
                                {t("deployments.multimodal")}
                              </Typography>
                              {value && (
                                <Chip
                                  label={t("createDeployment.selected")}
                                  color="primary"
                                  size="small"
                                  sx={{ mt: 1, height: 20, fontSize: "0.7rem" }}
                                />
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Controller
                      name="llm.is_image"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Card
                          sx={{
                            cursor: "pointer",
                            border: value ? "2px solid" : "1px solid",
                            borderColor: value ? "primary.main" : "divider",
                            height: "100%",
                            transition: "all 0.2s",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            },
                          }}
                          onClick={() => onChange(!value)}
                        >
                          <CardContent
                            sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <ImageIcon
                                sx={{
                                  fontSize: 32,
                                  mb: 1,
                                  color: value
                                    ? "primary.main"
                                    : "text.secondary",
                                }}
                              />
                              <Typography
                                variant="body2"
                                align="center"
                                fontWeight={500}
                              >
                                {t("deployments.image")}
                              </Typography>
                              {value && (
                                <Chip
                                  label={t("createDeployment.selected")}
                                  color="primary"
                                  size="small"
                                  sx={{ mt: 1, height: 20, fontSize: "0.7rem" }}
                                />
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Controller
                      name="llm.is_voice"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Card
                          sx={{
                            cursor: "pointer",
                            border: value ? "2px solid" : "1px solid",
                            borderColor: value ? "primary.main" : "divider",
                            height: "100%",
                            transition: "all 0.2s",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            },
                          }}
                          onClick={() => onChange(!value)}
                        >
                          <CardContent
                            sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <VoiceIcon
                                sx={{
                                  fontSize: 32,
                                  mb: 1,
                                  color: value
                                    ? "primary.main"
                                    : "text.secondary",
                                }}
                              />
                              <Typography
                                variant="body2"
                                align="center"
                                fontWeight={500}
                              >
                                {t("deployments.audioVoice")}
                              </Typography>
                              {value && (
                                <Chip
                                  label={t("createDeployment.selected")}
                                  color="primary"
                                  size="small"
                                  sx={{ mt: 1, height: 20, fontSize: "0.7rem" }}
                                />
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Controller
                      name="llm.is_video"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Card
                          sx={{
                            cursor: "pointer",
                            border: value ? "2px solid" : "1px solid",
                            borderColor: value ? "primary.main" : "divider",
                            height: "100%",
                            transition: "all 0.2s",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            },
                          }}
                          onClick={() => onChange(!value)}
                        >
                          <CardContent
                            sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <VideoIcon
                                sx={{
                                  fontSize: 32,
                                  mb: 1,
                                  color: value
                                    ? "primary.main"
                                    : "text.secondary",
                                }}
                              />
                              <Typography
                                variant="body2"
                                align="center"
                                fontWeight={500}
                              >
                                {t("deployments.video")}
                              </Typography>
                              {value && (
                                <Chip
                                  label={t("createDeployment.selected")}
                                  color="primary"
                                  size="small"
                                  sx={{ mt: 1, height: 20, fontSize: "0.7rem" }}
                                />
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Controller
                      name="llm.speculative_model"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Card
                          sx={{
                            cursor: "pointer",
                            border: value ? "2px solid" : "1px solid",
                            borderColor: value ? "primary.main" : "divider",
                            height: "100%",
                            transition: "all 0.2s",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            },
                          }}
                          onClick={() => onChange(!value)}
                        >
                          <CardContent
                            sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <SpeedIcon
                                sx={{
                                  fontSize: 32,
                                  mb: 1,
                                  color: value
                                    ? "primary.main"
                                    : "text.secondary",
                                }}
                              />
                              <Typography
                                variant="body2"
                                align="center"
                                fontWeight={500}
                              >
                                {t("deployments.speculativeDecoding")}
                              </Typography>
                              {value && (
                                <Chip
                                  label={t("createDeployment.selected")}
                                  color="primary"
                                  size="small"
                                  sx={{ mt: 1, height: 20, fontSize: "0.7rem" }}
                                />
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      )}
                    />
                  </Grid>

                  {isEmbedding && (
                    <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{
                          mt: 1,
                          p: { xs: 2, sm: 2.5 },
                          borderRadius: 3,
                          borderColor: "divider",
                          backgroundColor: "background.paper",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                          }}
                        >
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                gap: 1,
                              }}
                            >
                              <Typography variant="subtitle1">
                                {t("createDeployment.matryoshkaConfiguration")}
                              </Typography>

                              <Controller
                                name="llm.is_matryoshka"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                  <Switch
                                    checked={Boolean(value)}
                                    onChange={(event) =>
                                      onChange(event.target.checked)
                                    }
                                    color="secondary"
                                    inputProps={{
                                      "aria-label": t(
                                        "createDeployment.matryoshkaConfiguration",
                                      ),
                                    }}
                                  />
                                )}
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {t(
                                "createDeployment.matryoshkaConfigurationHelper",
                              )}
                            </Typography>
                          </Box>
                        </Box>

                        {isMatryoshka && (
                          <Box sx={{ mt: 2.5 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ mb: 1, fontWeight: 600 }}
                            >
                              {t("createDeployment.matryoshkaDimensions")}
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                alignItems: { xs: "stretch", sm: "flex-start" },
                                flexDirection: { xs: "column", sm: "row" },
                              }}
                            >
                              <Box
                                sx={{
                                  width: "100%",
                                  maxWidth: { sm: 380 },
                                }}
                              >
                                <TextField
                                  fullWidth
                                  label={t(
                                    "createDeployment.matryoshkaDimensions",
                                  )}
                                  placeholder={t(
                                    "createDeployment.matryoshkaDimensionsPlaceholder",
                                  )}
                                  value={matryoshkaDimensionInput}
                                  onChange={(event) => {
                                    setMatryoshkaDimensionInput(
                                      event.target.value,
                                    );

                                    if (errors.llm?.matryoshka_dimensions) {
                                      clearErrors("llm.matryoshka_dimensions");
                                    }
                                  }}
                                  onBlur={() =>
                                    commitPendingMatryoshkaDimensions()
                                  }
                                  onKeyDown={(event) => {
                                    if (
                                      event.key === "Enter" ||
                                      event.key === ","
                                    ) {
                                      event.preventDefault();
                                      commitPendingMatryoshkaDimensions();
                                    }
                                  }}
                                  error={Boolean(
                                    errors.llm?.matryoshka_dimensions,
                                  )}
                                  helperText={
                                    errors.llm?.matryoshka_dimensions
                                      ?.message ||
                                    t(
                                      "createDeployment.matryoshkaDimensionsHelper",
                                    )
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 2.5,
                                    },
                                  }}
                                />
                              </Box>

                              <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<AddIcon />}
                                onClick={() =>
                                  commitPendingMatryoshkaDimensions()
                                }
                                sx={{
                                  minWidth: { xs: "100%", sm: 120 },
                                  height: { sm: 56 },
                                  borderRadius: 2.5,
                                  alignSelf: { sm: "flex-start" },
                                  textTransform: "none",
                                  borderWidth: "1.5px",
                                  "&:hover": {
                                    borderWidth: "1.5px",
                                  },
                                }}
                              >
                                {t("common.add")}
                              </Button>
                            </Box>

                            {matryoshkaDimensions.length > 0 && (
                              <Box
                                sx={{
                                  mt: 2,
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                }}
                              >
                                {matryoshkaDimensions.map((dimension) => (
                                  <Chip
                                    key={dimension}
                                    label={dimension}
                                    color="secondary"
                                    variant="outlined"
                                    onDelete={() =>
                                      removeMatryoshkaDimension(dimension)
                                    }
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}

            {!isLLM && (
              <Grid item xs={12}>
                <Typography gutterBottom>
                  {t("deploymentDetail.deepLearningConfiguration")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="deeplearning.input_size"
                      control={control}
                      rules={{
                        required: t("createDeployment.inputSizeRequired"),
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={t("createDeployment.inputSize")}
                          fullWidth
                          margin="normal"
                          helperText={
                            fieldState.error
                              ? fieldState.error.message
                              : t("createDeployment.inputSizeHelper")
                          }
                          error={!!fieldState.error}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="deeplearning.max_batch_size"
                      control={control}
                      rules={{
                        required: t("createDeployment.maxBatchSizeRequired"),
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={t("createDeployment.maxBatchSize")}
                          type="number"
                          fullWidth
                          margin="normal"
                          helperText={
                            fieldState.error
                              ? fieldState.error.message
                              : t("createDeployment.maxBatchSizeHelper")
                          }
                          error={!!fieldState.error}
                          InputProps={{ inputProps: { min: 1 } }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="deeplearning.latency_constraint"
                      control={control}
                      rules={{
                        required: t(
                          "createDeployment.latencyConstraintRequired",
                        ),
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={t("createDeployment.latencyConstraint")}
                          type="number"
                          fullWidth
                          margin="normal"
                          helperText={
                            fieldState.error
                              ? fieldState.error.message
                              : t("createDeployment.latencyConstraintHelper")
                          }
                          error={!!fieldState.error}
                          InputProps={{ inputProps: { min: 0 } }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="deeplearning.throughput_constraint"
                      control={control}
                      rules={{
                        required: t(
                          "createDeployment.throughputConstraintRequired",
                        ),
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={t("createDeployment.throughputConstraint")}
                          type="number"
                          fullWidth
                          margin="normal"
                          helperText={
                            fieldState.error
                              ? fieldState.error.message
                              : t("createDeployment.throughputConstraintHelper")
                          }
                          error={!!fieldState.error}
                          InputProps={{ inputProps: { min: 0 } }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="deeplearning.gpu_used_memory"
                      control={control}
                      rules={{
                        required: t("createDeployment.gpuMemoryUsageRequired"),
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={t("createDeployment.gpuMemoryUsage")}
                          type="number"
                          fullWidth
                          margin="normal"
                          helperText={
                            fieldState.error
                              ? fieldState.error.message
                              : t("createDeployment.gpuMemoryUsageHelper")
                          }
                          error={!!fieldState.error}
                          InputProps={{ inputProps: { min: 0 } }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("createDeployment.resourceConfiguration")}
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12}>
              <Typography gutterBottom>
                {t("createDeployment.gpuConfiguration")}
              </Typography>

              {loadingGpuRates ? (
                <Box sx={{ px: 2 }}>
                  <Grid container spacing={2}>
                    {[1, 2, 3, 4].map((index) => (
                      <Grid item xs={12} sm={6} md={3} key={index}>
                        <Skeleton variant="rounded" height={180} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ) : gpuRates.length === 0 ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {t("createDeployment.gpuOptionsLoadError")}
                </Alert>
              ) : (
                <>
                  {/* GPU Type Selection */}
                  <Box sx={{ mb: 3 }}>
                    <Controller
                      name="gpu_type"
                      control={control}
                      rules={{
                        required: t("createDeployment.gpuTypeRequired"),
                      }}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.gpu_type}>
                          <InputLabel id="gpu-type-label">
                            {t("predefinedModels.gpuType")}
                          </InputLabel>
                          <Select
                            {...field}
                            labelId="gpu-type-label"
                            label={t("predefinedModels.gpuType")}
                          >
                            {gpuRates.map((rate) => (
                              <MenuItem
                                key={rate.gpu_type}
                                value={rate.gpu_type}
                              >
                                {t("createDeployment.gpuRateOption", {
                                  gpuType: rate.gpu_type,
                                  price: rate.hourly_rate.toFixed(2),
                                })}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.gpu_type && (
                            <FormHelperText>
                              {errors.gpu_type.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Box>

                  {/* GPU Count Selection */}
                  <Box sx={{ px: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("createDeployment.numberOfGpus")}
                    </Typography>
                    <Controller
                      name="gpu_count"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Grid container spacing={2}>
                          {[1, 2, 4, 8].map((count) => {
                            // Find the selected GPU rate
                            const selectedGpuRate = gpuRates.find(
                              (rate) => rate.gpu_type === gpuType,
                            );
                            const price = selectedGpuRate
                              ? selectedGpuRate.hourly_rate * count
                              : 0;
                            const totalMemory = `${count * 16}GB`; // Assuming 16GB per GPU, adjust as needed

                            return (
                              <Grid item xs={12} sm={6} md={3} key={count}>
                                <Card
                                  sx={{
                                    cursor: "pointer",
                                    border:
                                      value === count
                                        ? "2px solid"
                                        : "1px solid",
                                    borderColor:
                                      value === count
                                        ? "primary.main"
                                        : "divider",
                                    transition: "transform 0.2s",
                                    "&:hover": {
                                      transform: "translateY(-4px)",
                                      boxShadow:
                                        "0 8px 24px rgba(0, 0, 0, 0.15)",
                                    },
                                  }}
                                  onClick={() => {
                                    onChange(count);
                                    // Update memory based on GPU count
                                    setValue(
                                      "memory_request",
                                      `${count * 16}Gi`,
                                    );
                                    setValue("memory_limit", `${count * 16}Gi`);
                                  }}
                                >
                                  <CardContent>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 1,
                                      }}
                                    >
                                      <Typography variant="h6">
                                        {count === 1
                                          ? t("createDeployment.singleGpu")
                                          : t(
                                              "createDeployment.gpuCountValue",
                                              {
                                                count,
                                              },
                                            )}
                                      </Typography>
                                      {value === count && (
                                        <Chip
                                          label={t("createDeployment.selected")}
                                          color="primary"
                                          size="small"
                                        />
                                      )}
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 1,
                                      }}
                                    >
                                      <MemoryIcon
                                        sx={{
                                          marginInlineEnd: 1,
                                          color: "primary.main",
                                        }}
                                      />
                                      <Typography variant="body2">
                                        {t("createDeployment.memoryAmount", {
                                          memory: totalMemory,
                                        })}
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="h5"
                                      color="secondary.main"
                                      sx={{ mt: 2 }}
                                    >
                                      {t("createDeployment.pricePerHour", {
                                        price: price.toFixed(2),
                                      })}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Grid>
                            );
                          })}
                        </Grid>
                      )}
                    />
                  </Box>
                </>
              )}
            </Grid>

            {!isLLM && (
              <Grid item xs={12}>
                <Typography gutterBottom>
                  {t("createDeployment.deepLearningModelConfiguration")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="deeplearning.input_size"
                      control={control}
                      rules={{
                        required: t("createDeployment.inputSizeRequired"),
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={t("createDeployment.inputSize")}
                          fullWidth
                          margin="normal"
                          helperText={
                            fieldState.error
                              ? fieldState.error.message
                              : t("createDeployment.inputSizeHelper")
                          }
                          error={!!fieldState.error}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="deeplearning.max_batch_size"
                      control={control}
                      rules={{
                        required: t("createDeployment.maxBatchSizeRequired"),
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={t("createDeployment.maxBatchSize")}
                          type="number"
                          fullWidth
                          margin="normal"
                          helperText={
                            fieldState.error
                              ? fieldState.error.message
                              : t("createDeployment.maxBatchSizeHelper")
                          }
                          error={!!fieldState.error}
                          InputProps={{ inputProps: { min: 1 } }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="deeplearning.latency_constraint"
                      control={control}
                      rules={{
                        required: t(
                          "createDeployment.latencyConstraintRequired",
                        ),
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={t("createDeployment.latencyConstraint")}
                          type="number"
                          fullWidth
                          margin="normal"
                          helperText={
                            fieldState.error
                              ? fieldState.error.message
                              : t("createDeployment.latencyConstraintHelper")
                          }
                          error={!!fieldState.error}
                          InputProps={{ inputProps: { min: 0 } }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="deeplearning.throughput_constraint"
                      control={control}
                      rules={{
                        required: t(
                          "createDeployment.throughputConstraintRequired",
                        ),
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={t("createDeployment.throughputConstraint")}
                          type="number"
                          fullWidth
                          margin="normal"
                          helperText={
                            fieldState.error
                              ? fieldState.error.message
                              : t("createDeployment.throughputConstraintHelper")
                          }
                          error={!!fieldState.error}
                          InputProps={{ inputProps: { min: 0 } }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="deeplearning.gpu_used_memory"
                      control={control}
                      rules={{
                        required: t("createDeployment.gpuMemoryUsageRequired"),
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          label={t("createDeployment.gpuMemoryUsage")}
                          type="number"
                          fullWidth
                          margin="normal"
                          helperText={
                            fieldState.error
                              ? fieldState.error.message
                              : t("createDeployment.gpuMemoryUsageHelper")
                          }
                          error={!!fieldState.error}
                          InputProps={{ inputProps: { min: 0 } }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography gutterBottom>
                {t("createDeployment.advancedResourceConfiguration")}
              </Typography>
              <Alert severity="info" sx={{ mb: 2, fontSize: "14px" }}>
                {t("createDeployment.advancedResourceConfigurationHelper")}
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="cpu_request"
                    control={control}
                    rules={{
                      required: t("createDeployment.cpuRequestRequired"),
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t("deploymentDetail.cpuRequest")}
                        fullWidth
                        error={!!errors.cpu_request}
                        helperText={
                          errors.cpu_request?.message ||
                          t("common.exampleValue", { value: "1, 2, 0.5" })
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="cpu_limit"
                    control={control}
                    rules={{ required: t("createDeployment.cpuLimitRequired") }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t("deploymentDetail.cpuLimit")}
                        fullWidth
                        error={!!errors.cpu_limit}
                        helperText={
                          errors.cpu_limit?.message ||
                          t("common.exampleValue", { value: "2, 4, 1" })
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="memory_request"
                    control={control}
                    rules={{
                      required: t("createDeployment.memoryRequestRequired"),
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t("deploymentDetail.memoryRequest")}
                        fullWidth
                        error={!!errors.memory_request}
                        helperText={
                          errors.memory_request?.message ||
                          t("common.exampleValue", { value: "16Gi, 32Gi" })
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="memory_limit"
                    control={control}
                    rules={{
                      required: t("createDeployment.memoryLimitRequired"),
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t("deploymentDetail.memoryLimit")}
                        fullWidth
                        error={!!errors.memory_limit}
                        helperText={
                          errors.memory_limit?.message ||
                          t("common.exampleValue", { value: "16Gi, 32Gi" })
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="storage"
                    control={control}
                    rules={{ required: t("createDeployment.storageRequired") }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t("predefinedModels.storage")}
                        fullWidth
                        error={!!errors.storage}
                        helperText={
                          errors.storage?.message ||
                          t("common.exampleValue", {
                            value: "15Gi, 30Gi, 50Gi",
                          })
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("deploymentDetail.credentials")}
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            {modelSource === "hf" && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t("deploymentDetail.hfCredentialsHint")}
                </Alert>
                {loadingCredentials ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", my: 2 }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Box sx={{ flex: 1, mb: 0 }}>
                        <Controller
                          name="credential_id"
                          control={control}
                          render={({ field }) => (
                            <FormControl fullWidth sx={{ mb: 0 }}>
                              <InputLabel id="hf-credential-label">
                                {t("deploymentDetail.hfCredentials")}
                              </InputLabel>
                              <Select
                                {...field}
                                labelId="hf-credential-label"
                                label={t(
                                  "createDeployment.huggingFaceCredentials",
                                )}
                                sx={{ height: "40px" }}
                              >
                                <MenuItem value="">
                                  <em>
                                    {t("createDeployment.nonePublicModelsOnly")}
                                  </em>
                                </MenuItem>
                                {credentials.map((cred) => (
                                  <MenuItem key={cred.id} value={cred.id}>
                                    {cred.name || `Credential #${cred.id}`}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Box>
                      <Box sx={{ width: "120px" }}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<AddIcon />}
                          onClick={handleOpenCredentialDialog}
                          fullWidth
                          sx={{
                            height: "40px",
                            textTransform: "none",
                            boxShadow: 0,
                            borderWidth: "2px",
                            "&:hover": {
                              borderWidth: "2px",
                              boxShadow: 2,
                            },
                          }}
                        >
                          {t("common.addNew")}
                        </Button>
                      </Box>
                    </Box>
                    <Box sx={{ marginInlineStart: "0", mt: 1 }}>
                      <FormHelperText>
                        {t("createDeployment.huggingFaceCredentialsHelper")}
                      </FormHelperText>
                    </Box>
                  </>
                )}
              </Grid>
            )}

            {modelSource === "s3" && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t("deploymentDetail.s3CredentialsHint")}
                </Alert>
                {loadingCredentials ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", my: 2 }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Box sx={{ flex: 1, mb: 0 }}>
                        <Controller
                          name="credential_id"
                          control={control}
                          rules={{
                            required: t(
                              "createDeployment.awsCredentialsRequired",
                            ),
                          }}
                          render={({ field }) => (
                            <FormControl
                              fullWidth
                              error={!!errors.credential_id}
                              sx={{ mb: 0 }}
                            >
                              <InputLabel id="s3-credential-label">
                                {t("deploymentDetail.awsS3Credentials")}
                              </InputLabel>
                              <Select
                                {...field}
                                labelId="s3-credential-label"
                                label={t("createDeployment.awsS3Credentials")}
                                sx={{ height: "40px" }}
                              >
                                {credentials.length > 0 ? (
                                  credentials.map((cred) => (
                                    <MenuItem key={cred.id} value={cred.id}>
                                      {cred.name || `Credential #${cred.id}`}
                                    </MenuItem>
                                  ))
                                ) : (
                                  <MenuItem disabled value="">
                                    <em>
                                      {t(
                                        "createDeployment.noCredentialsAvailable",
                                      )}
                                    </em>
                                  </MenuItem>
                                )}
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Box>
                      <Box sx={{ width: "120px" }}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<AddIcon />}
                          onClick={handleOpenCredentialDialog}
                          fullWidth
                          sx={{
                            height: "40px",
                            textTransform: "none",
                            boxShadow: 0,
                            borderWidth: "2px",
                            "&:hover": {
                              borderWidth: "2px",
                              boxShadow: 2,
                            },
                          }}
                        >
                          {t("common.addNew")}
                        </Button>
                      </Box>
                    </Box>
                    <Box sx={{ marginInlineStart: "0", mt: 1 }}>
                      <FormHelperText error={!!errors.credential_id}>
                        {errors.credential_id
                          ? errors.credential_id.message
                          : t("createDeployment.awsCredentialsHelper")}
                      </FormHelperText>
                    </Box>
                  </>
                )}
                {credentials.length === 0 && !loadingCredentials && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {t("deploymentDetail.noAwsCredentials")}
                  </Alert>
                )}
              </Grid>
            )}

            {/* SECURITY FIX: Commented out HTTP URL credentials section to prevent SSRF vulnerability */}
            {/* {modelSource === 'http' && (
              <Grid item xs={12}>
                <Alert severity="info">
                  No additional credentials are required for HTTP URLs. Make sure your model is publicly accessible.
                </Alert>
              </Grid>
            )} */}
          </Grid>
        );

      case 3:
        const formData = getValues();
        const hasSelectedLlmCapabilities =
          formData.llm.is_embedding ||
          formData.llm.is_multimodal ||
          formData.llm.is_image ||
          formData.llm.is_voice ||
          formData.llm.is_video ||
          formData.llm.speculative_model ||
          formData.llm.is_matryoshka ||
          (formData.llm.matryoshka_dimensions?.length || 0) > 0;
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("createDeployment.reviewAndDeploy")}
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t("createDeployment.deploymentSummary")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("deployments.modelName")}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formData.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("deployments.modelType")}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formData.is_llm
                        ? t("deployments.llm")
                        : t("createDeployment.computerVisionDeepLearning")}
                    </Typography>
                  </Grid>
                  {formData.is_llm && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t("deployments.llmCapabilities")}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        {formData.llm.is_embedding && (
                          <Chip
                            size="small"
                            label={t("deployments.embedding")}
                          />
                        )}
                        {formData.llm.is_multimodal && (
                          <Chip
                            size="small"
                            label={t("deployments.multimodal")}
                          />
                        )}
                        {formData.llm.is_image && (
                          <Chip size="small" label={t("deployments.image")} />
                        )}
                        {formData.llm.is_voice && (
                          <Chip
                            size="small"
                            label={t("deployments.audioVoice")}
                          />
                        )}
                        {formData.llm.is_video && (
                          <Chip size="small" label={t("deployments.video")} />
                        )}
                        {formData.llm.speculative_model && (
                          <Chip
                            size="small"
                            label={t("deployments.speculativeDecoding")}
                          />
                        )}
                        {formData.llm.is_matryoshka && (
                          <Chip
                            size="small"
                            label={t("deployments.matryoshka")}
                          />
                        )}
                        {formData.llm.matryoshka_dimensions?.length > 0 && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t(
                              "createDeployment.matryoshkaDimensionsChip",
                              {
                                dimensions:
                                  formData.llm.matryoshka_dimensions.join(", "),
                              },
                            )}
                          />
                        )}
                        {!hasSelectedLlmCapabilities && (
                          <Typography variant="body2" color="text.secondary">
                            {t(
                              "createDeployment.noSpecialCapabilitiesSelected",
                            )}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("deployments.modelSource")}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {modelSource === "hf"
                        ? t("deployments.huggingFace")
                        : modelSource === "s3"
                          ? t("deployments.s3Bucket")
                          : t("createDeployment.other")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("deployments.modelUrl")}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formData.model_url}
                    </Typography>
                  </Grid>
                  {formData.credential_id && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t("createDeployment.credentials")}
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {credentials.find(
                          (cred) => cred.id === formData.credential_id,
                        )?.name || t("createDeployment.selectedCredential")}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("createDeployment.gpuConfiguration")}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {t("createDeployment.gpuConfigurationValue", {
                        gpuType: formData.gpu_type,
                        count: formData.gpu_count,
                      })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("predefinedModels.storage")}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formData.storage}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("deployments.maximumReplicas")}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formData.max_replicas}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 3, mb: 3, bgcolor: "background.paper" }}>
                <Typography variant="h6" gutterBottom>
                  {t("deploymentDetail.pricingInformation")}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="body1">
                    {t("createDeployment.hourlyCost")}
                  </Typography>
                  <Typography variant="h5" color="secondary.main">
                    {t("createDeployment.pricePerHour", {
                      price: calculateHourlyCost().toFixed(2),
                    })}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="body1">
                    {t("createDeployment.estimatedMonthlyCost")}
                  </Typography>
                  <Typography variant="h6" color="secondary.main">
                    {t("createDeployment.pricePerMonth", {
                      price: (calculateHourlyCost() * 24 * 30).toFixed(2),
                    })}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t("deploymentDetail.chargedOnlyWhenRunning")}
                </Typography>
              </Paper>
            </Grid>

            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
          </Grid>
        );

      default:
        return t("createDeployment.unknownStep");
    }
  };

  return (
    <DashboardLayout title={t("createDeployment.title")}>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <div>
          {getStepContent(activeStep)}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button
              disabled={activeStep === 0 || loading}
              onClick={handleBack}
              startIcon={isRTL ? <ArrowForwardIcon /> : <ArrowBackIcon />}
              variant="outlined"
            >
              {t("common.back")}
            </Button>
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmitDeployment}
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <CloudUploadIcon />
                    )
                  }
                >
                  {loading
                    ? t("createDeployment.deploying")
                    : t("deployments.deployModel")}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  endIcon={!isRTL ? <ArrowForwardIcon /> : <ArrowBackIcon />}
                >
                  {t("common.next")}
                </Button>
              )}
            </Box>
          </Box>
        </div>
      </Paper>

      {/* Dialog for creating new credentials */}
      <Dialog
        open={openCredentialDialog}
        onClose={handleCloseCredentialDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("createDeployment.addNewCredentials", {
            source: modelSource === "hf" ? "Hugging Face" : "AWS S3",
          })}
          <IconButton
            onClick={handleCloseCredentialDialog}
            sx={{ position: "absolute", insetInlineEnd: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label={t("createDeployment.credentialName")}
                fullWidth
                value={newCredential.name}
                onChange={handleCredentialInputChange}
                placeholder={t("createDeployment.credentialNamePlaceholder", {
                  source: modelSource === "hf" ? "Hugging Face" : "AWS",
                })}
                helperText={
                  (touchedFields.name && credentialFormErrors.name) ||
                  t("createDeployment.credentialNameHelper")
                }
                error={touchedFields.name && !!credentialFormErrors.name}
                required
              />
            </Grid>

            {modelSource === "hf" && (
              <Grid item xs={12}>
                <TextField
                  name="data.token"
                  label={t("createDeployment.huggingFaceToken")}
                  fullWidth
                  type="password"
                  value={newCredential.data.token}
                  onChange={handleCredentialInputChange}
                  placeholder={t(
                    "createDeployment.huggingFaceTokenPlaceholder",
                  )}
                  helperText={
                    (touchedFields.token && credentialFormErrors.token) ||
                    t("createDeployment.huggingFaceTokenHelper")
                  }
                  error={touchedFields.token && !!credentialFormErrors.token}
                  required
                />
              </Grid>
            )}

            {modelSource === "s3" && (
              <>
                <Grid item xs={12}>
                  <TextField
                    name="data.aws_access_key_id"
                    label={t("deploymentDetail.awsAccessKeyId")}
                    fullWidth
                    value={newCredential.data.aws_access_key_id}
                    onChange={handleCredentialInputChange}
                    error={
                      touchedFields.aws_access_key_id &&
                      !!credentialFormErrors.aws_access_key_id
                    }
                    helperText={
                      (touchedFields.aws_access_key_id &&
                        credentialFormErrors.aws_access_key_id) ||
                      ""
                    }
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="data.aws_secret_access_key"
                    label={t("deploymentDetail.awsSecretAccessKey")}
                    fullWidth
                    type="password"
                    value={newCredential.data.aws_secret_access_key}
                    onChange={handleCredentialInputChange}
                    error={
                      touchedFields.aws_secret_access_key &&
                      !!credentialFormErrors.aws_secret_access_key
                    }
                    helperText={
                      (touchedFields.aws_secret_access_key &&
                        credentialFormErrors.aws_secret_access_key) ||
                      ""
                    }
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="data.url_endpoint"
                    label={t("createDeployment.customUrlEndpointOptional")}
                    fullWidth
                    value={newCredential.data.url_endpoint || ""}
                    onChange={handleCredentialInputChange}
                    placeholder={t("createDeployment.customUrlPlaceholder")}
                    helperText={t("createDeployment.customUrlEndpointHelper")}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    name="data.region"
                    label={t("deploymentDetail.awsRegion")}
                    fullWidth
                    value={newCredential.data.region}
                    onChange={handleCredentialInputChange}
                  >
                    <MenuItem value="us-east-1">
                      {t("deploymentDetail.awsRegionUSEast1")}
                    </MenuItem>
                    <MenuItem value="us-east-2">
                      {t("deploymentDetail.awsRegionUSEast2")}
                    </MenuItem>
                    <MenuItem value="us-west-1">
                      {t("deploymentDetail.awsRegionUSWest1")}
                    </MenuItem>
                    <MenuItem value="us-west-2">
                      {t("deploymentDetail.awsRegionUSWest2")}
                    </MenuItem>
                    <MenuItem value="eu-west-1">
                      {t("deploymentDetail.awsRegionEUWest1")}
                    </MenuItem>
                    <MenuItem value="eu-central-1">
                      {t("deploymentDetail.awsRegionEUCentral1")}
                    </MenuItem>
                    <MenuItem value="ap-northeast-1">
                      {t("deploymentDetail.awsRegionAPNortheast1")}
                    </MenuItem>
                    <MenuItem value="ap-southeast-1">
                      {t("deploymentDetail.awsRegionAPSoutheast1")}
                    </MenuItem>
                  </TextField>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseCredentialDialog}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateCredential}
            disabled={creatingCredential || !isCredentialFormValid}
            startIcon={
              creatingCredential ? <CircularProgress size={20} /> : null
            }
          >
            {creatingCredential
              ? t("common.creating")
              : t("createDeployment.createCredentials")}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default CreateDeployment;
